import { ReactElement, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/_atoms/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/_atoms/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/_atoms/tooltip";
import IconCompose from "@/components/_icons/IconCompose";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { ModuleActionGuard } from "@/domain/_shared/module-access/components/module-action-guard";
import { useCheckPermission } from "@/domain/_shared/permission/hooks/api/use-check-permission";
import { useCreateMunicipalProperty } from "@/domain/municipal-property/hooks/api/use-create-municipal-property";
import { MunicipalPropertyCreateFormValues } from "@/domain/municipal-property/models/municipal-property-create-form-values";
import { toCreateMunicipalPropertyInput } from "@/domain/municipal-property/models/to-create-municipal-property-input";
import { CurrentWorkspace } from "@/domain/workspace/models/current-workspace-model";
import { extractGraphQLErrorCode } from "@/lib/extract-graphql-error-code";

import { MunicipalPropertyCreateForm } from "./municipal-property-create-form";
import { MunicipalPropertyCsvImport } from "./municipal-property-csv-import";

interface MunicipalPropertyCreateDialogProps {
  workspace: CurrentWorkspace;
}

export function MunicipalPropertyCreateDialog({ workspace }: MunicipalPropertyCreateDialogProps): ReactElement | null {
  const { t } = useDomainTranslation("municipal-property");
  const [isOpen, setIsOpen] = useState(false);
  const create = useCreateMunicipalProperty();

  async function handleSubmit(values: MunicipalPropertyCreateFormValues): Promise<void> {
    await create
      .mutateAsync(toCreateMunicipalPropertyInput({ workspaceId: workspace.id, values }), {
        onSuccess: () => {
          toast.success(t("create.success"));
          setIsOpen(false);
        },
        onError: (error) => {
          toast.error(t(extractGraphQLErrorCode(error) === "CONFLICT" ? "history.conflict" : "create.error"));
        },
      })
      .catch(() => undefined);
  }

  const { hasPermission: canManageWorkspace } = useCheckPermission({
    resource: "workspace",
    action: "manage",
    resourceId: workspace.id,
  });

  if (!canManageWorkspace) {
    return null;
  }

  return (
    <>
      <ModuleActionGuard module="MUNICIPAL_PROPERTY" action="create">
        <MunicipalPropertyCsvImport workspaceId={workspace.id} municipalityList={workspace.municipalityList ?? []} />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="xs"
                aria-label={t("catalog.createButton")}
                onClick={() => setIsOpen(true)}
                data-testid="create-municipal-property-button"
              >
                <IconCompose className="mr-2" />
                {t("catalog.createButton")}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("catalog.createButton")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </ModuleActionGuard>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("create.dialogTitle")}</DialogTitle>
            <DialogDescription>{t("create.dialogDescription")}</DialogDescription>
          </DialogHeader>
          <MunicipalPropertyCreateForm
            municipalityList={workspace.municipalityList ?? []}
            submitLabel={t("create.submit")}
            cancelLabel={t("create.cancel")}
            onSubmit={handleSubmit}
            onCancel={() => setIsOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
